import axios, { AxiosResponse } from "axios";
import { put, select, delay } from "redux-saga/effects";
import dayjs from "dayjs";
import {
  AIR_PRICE_QUOTE_TO_BOOK_REQUEST_RUNTIME,
  ConnectionResultEnum,
  InvalidEnum,
  CallState,
  TravelWalletOffer,
} from "redmond";
import {
  CipherText,
  Payment,
  PaymentOpaqueValue,
  purchaseApi,
} from "@b2bportal/purchase-api";
import { getHSPEarnOfferDescription, isEarnOfferV2, isSpendAndGetOffer } from "halifax";
import { getIsTravelApp, getUserDeviceData } from "@capone/common";
import { actions } from "../../actions";
import {
  getPriceQuoteAirProperties,
  getPriceQuoteSuccessTime,
  getIsWaitingPriceQuote,
  getPollPriceQuoteCallState,
  getPriceDifference,
  getPaymentsV2,
  getSession,
  getApprovalRequestReason,
  getTripDescription,
  getOffers,
} from "../../reducer";
import { isFlightBookWithAncillariesActiveSelector } from "../../../shop/reducer";
import { delayTimes } from "./pollQuoteSaga";
import { IStoreState } from "../../../../reducers/types";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { authorizeEarnOffer } from "../../../../api/v0/travel-wallet/authorizeEarnOffer";

export function* schedulePaymentSaga({
  session,
  payments,
  ancillaries,
  agentFee,
  initialTime,
}: actions.ISetSchedulePayment) {
  try {
    const isFlightBookWithAncillariesActive: boolean = yield select(
      isFlightBookWithAncillariesActiveSelector
    );
    /*
      note: in the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting
      a price quote could still be in progress at this step; therefore, schedulePayment needs to be postponed until the new price quote returns
    */
    let result: WaitResult | undefined;
    if (isFlightBookWithAncillariesActive) {
      result = yield waitForPriceQuote();
    }
    if (result === WaitResult.Terminate) {
      return;
    }

    /*
      note: allowing the consumer to avoid providing the session & payments at the call time; also, session & payments passed through
      the action call will have the highest priority, so it's not affecting any existing code path where session & payments are provided
    */
    const currentSession: CipherText | undefined =
      session ?? (yield select(getSession));
    const currentPayments: PaymentOpaqueValue[] | undefined | null =
      payments ?? (yield select(getPaymentsV2));
    const corporateOutOfPolicyReason: string = yield select(
      getApprovalRequestReason
    );
    const corporateTripDescription: string | undefined = yield select(
      getTripDescription
    );
    if (!currentSession || !currentPayments || currentPayments.length === 0) {
      // note: this code path is only hit by the PF purchase flow; until there is an appropriate error modal to show, setting the call state to NotCalled is good enough
      yield put(actions.setSchedulePaymentNotCalled());
      return;
    }

    const offers: TravelWalletOffer[] | undefined = yield select(getOffers);
    const appliedOfferPayment = payments?.find(
      (payment) => payment.type === Payment.TravelWalletOffer
    );

    const appliedOffer = offers?.find(
      (offer) => offer.id === appliedOfferPayment?.value.offerId
    );

    const hspEarnDescription = getHSPEarnOfferDescription(appliedOffer);
    const isV2EarnOffer = isEarnOfferV2(appliedOffer);
    const isSpendAndGet =
      appliedOffer && isSpendAndGetOffer(appliedOffer.descriptions);

    // authorize hsp earn offer before fulfillment (not for spend-and-get offers or v2 earn offers)
    if (hspEarnDescription && !isV2EarnOffer && appliedOffer?.id) {
      yield authorizeEarnOffer({
        earnOfferId: appliedOffer.id,
      });
    }
    const { isTravelApp } = getIsTravelApp();

    const userDeviceData = getUserDeviceData();

    const channel = (() => {
      switch (true) {
        case isTravelApp:
          return "TravelApp";
        case userDeviceData.deviceType === "mobile":
          return "Mobile";
        default:
          return "Web";
      }
    })();

    // Don't include legacy HSP earn offers or spend-and-get offers in payments.
    // v2 earn offers are sent as regular payments.
    const shouldFilterOffer =
      (hspEarnDescription && !isV2EarnOffer) || isSpendAndGet;

    const request = () =>
      purchaseApi(axios as any).apiV0PurchaseFulfillSchedulePost({
        quoteToken: currentSession,
        payments: shouldFilterOffer
          ? currentPayments.filter(
              (payment) => payment.type !== Payment.TravelWalletOffer
            )
          : currentPayments,
        ancillaries,
        corporateOutOfPolicyReason,
        corporateTripDescription,
        channel,
      });
    const response: AxiosResponse<CipherText> = yield request();

    const state: IStoreState = yield select();
    const priceQuoteSuccessTime = dayjs(getPriceQuoteSuccessTime(state));
    const priceQuoteAirProps = getPriceQuoteAirProperties(state);
    trackEvent({
      eventName: AIR_PRICE_QUOTE_TO_BOOK_REQUEST_RUNTIME,
      properties: {
        runtime: dayjs().diff(priceQuoteSuccessTime, "seconds", true),
        provider: priceQuoteAirProps?.provider || "",
        entryType: priceQuoteAirProps?.entry_type,
        entryTypesAll: priceQuoteAirProps?.entry_types_all,
      },
    });

    yield put(actions.setSession(response.data));
    yield put(actions.setSchedulePaymentSuccess());
    yield put(actions.pollFinalized(agentFee, initialTime));
  } catch (e) {
    yield put(
      actions.setSchedulePaymentFailed({
        Invalid: InvalidEnum.Missing,
        ConnectionResult: ConnectionResultEnum.Invalid,
      })
    );
  }
}

enum WaitResult {
  Proceed,
  Terminate,
}

function* waitForPriceQuote() {
  let pollTerminated = false;
  let totalDelayTime = delayTimes.reduce<number>(
    (sum, current) => sum + current,
    0
  );

  while (!pollTerminated) {
    const state: IStoreState = yield select();
    const isWaitingPriceQuote = getIsWaitingPriceQuote(state);
    const pollPriceQuoteCallState = getPollPriceQuoteCallState(state);
    const priceDifference = getPriceDifference(state);

    if (!isWaitingPriceQuote || totalDelayTime <= 0) {
      pollTerminated = true;

      // note: when confirm-and-book isn't ready by the end, it will terminate this saga
      if (
        pollPriceQuoteCallState === CallState.Failed ||
        priceDifference.hasDifference
      ) {
        yield put(
          actions.setSchedulePaymentFailed({
            Invalid: InvalidEnum.Missing,
            ConnectionResult: ConnectionResultEnum.Invalid,
          })
        );
        return WaitResult.Terminate;
      }
    } else {
      // checks the progress of poll quote every second
      totalDelayTime -= 1000;
      yield delay(1000);
    }
  }

  return WaitResult.Proceed;
}
